import { Box, Container, Flex, Heading, Image, SimpleGrid, Stack, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { CtaButton } from '../components/CtaButton';

const STEPS = ['s1', 's2', 's3', 's4', 's5'] as const;

// Steps 2-5 use the exported Figma UI mockups; step 1 (a simple form) is the
// one Figma renders from vectors, reproduced in CSS.
const STEP_IMG: Record<string, string> = {
  s2: '/images/landing/how-step2.png',
  s3: '/images/landing/how-step3.png',
  s4: '/images/landing/how-step4.png',
  s5: '/images/landing/how-step5.png',
};

function StepIllustration({ step }: { step: string }) {
  if (step === 's1') {
    return (
      <Flex bg="white" borderRadius="10px" p="11px" h="73px" align="center" gap="10px">
        <Box w="39px" h="39px" borderRadius="full" bg="onboarding.cream" flexShrink={0} />
        <Stack spacing="7px" flex="1">
          <Box h="23px" borderRadius="full" bg="onboarding.cream" />
          <Box h="23px" borderRadius="full" bg="onboarding.cream" w="78%" />
        </Stack>
      </Flex>
    );
  }
  return (
    <Flex h="73px" align="center" justify="center">
      <Image src={STEP_IMG[step]} alt="" maxH="73px" maxW="100%" borderRadius="10px" draggable={false} />
    </Flex>
  );
}

function StepCard({ s }: { s: string }) {
  const { t } = useTranslation();
  return (
    <Stack bg="onboarding.cream" borderRadius="20px" p="20px" spacing="14px" minH="240px" justify="space-between">
      <Stack spacing="6px">
        <Text fontSize="md" fontWeight={700} textTransform="uppercase" color="onboarding.roseFaint">
          {t(`landing.how.steps.${s}.label`)}
        </Text>
        <Text fontSize="md" fontWeight={600} color="onboarding.deepPlum">
          {t(`landing.how.steps.${s}.title`)}
        </Text>
      </Stack>
      <StepIllustration step={s} />
      <Box>
        <Text
          as="span"
          display="inline-block"
          fontSize="sm"
          fontWeight={500}
          bg="white"
          color="onboarding.mutedRose"
          px="14px"
          py="3px"
          borderRadius="full"
        >
          {t(`landing.how.steps.${s}.tag`)}
        </Text>
      </Box>
    </Stack>
  );
}

export function HowItWorks() {
  const { t } = useTranslation();
  return (
    <Box as="section" id="how-it-works" bg="white">
      <Container maxW="1240px" py={{ base: 16, lg: 24 }}>
        <Stack spacing="10px" textAlign="center" align="center" mb={{ base: 10, lg: 14 }}>
          <Heading
            as="h2"
            fontFamily="heading"
            fontWeight={700}
            fontSize="display.lg"
            lineHeight={1.1}
            letterSpacing="-0.01em"
            color="onboarding.deepPlum"
          >
            {t('landing.how.title')}
          </Heading>
          <Text fontSize="md" color="onboarding.roseMid">
            {t('landing.how.subtitle')}
          </Text>
        </Stack>
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 5 }} spacing="10px" maxW="1190px" mx="auto">
          {STEPS.map((s) => (
            <StepCard key={s} s={s} />
          ))}
        </SimpleGrid>
        <Flex justify="center" mt={{ base: 10, lg: 14 }}>
          <CtaButton
            to="/onboarding"
            bg="onboarding.plum"
            color="white"
            borderRadius="pill"
            height="48px"
            px="28px"
            fontSize="md"
            fontWeight={600}
            _hover={{ bg: 'onboarding.roseDeep' }}
            withArrow
            trackEventName="cta_click"
            trackProps={{ section: 'how-it-works' }}
          >
            {t('landing.how.cta')}
          </CtaButton>
        </Flex>
      </Container>
    </Box>
  );
}
